import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";

export enum Theme {
  Light = "light",
  Dark = "dark",
}

interface ThemeContextType {
  theme: Theme;
  onSetTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme = Theme.Light }) => {
  // Read initial theme from HTML element (set by inline script in index.html)
  const getInitialTheme = (): Theme => {
    const dataTheme = document.documentElement.getAttribute("data-theme");
    if (dataTheme === Theme.Dark) return Theme.Dark;
    if (dataTheme === Theme.Light) return Theme.Light;
    return initialTheme;
  };

  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  const onSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, onSetTheme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
